import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/onboarding :: StepIndicator
 *
 * Tiny progress bar + textual counter used at the top of the wizard. Exposes
 * `role="progressbar"` with numeric aria-valuemin / valuenow / valuemax so
 * screen readers can announce "step 3 of 5". Sighted users see a thin bar +
 * the "Step 3 of 5" counter immediately next to it.
 *
 * Pure presentation — the wizard owns the numbers and re-renders this on
 * every step change.
 */
export interface StepIndicatorProps {
  /** 1-based current step. Clamped to `[1, total]`. */
  current: number;
  /** Total number of steps visible to the user. */
  total: number;
}

export default function StepIndicator({ current, total }: StepIndicatorProps) {
  const { t } = useTranslation();
  const safeTotal = Math.max(1, Math.floor(total));
  const safeCurrent = Math.max(1, Math.min(safeTotal, Math.floor(current)));
  const percent = Math.round((safeCurrent / safeTotal) * 100);

  return (
    <div className="flex items-center gap-sm">
      <div
        role="progressbar"
        aria-valuemin={1}
        aria-valuenow={safeCurrent}
        aria-valuemax={safeTotal}
        aria-label={t("onboarding.stepIndicator.label")}
        className="relative h-2 flex-1 overflow-hidden rounded-full bg-brand-primary/10"
      >
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 bg-brand-primary transition-[width] duration-200"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span
        data-testid="onboarding-step-counter"
        className="shrink-0 text-xs font-medium text-neutral-700"
      >
        {t("onboarding.stepIndicator.counter", {
          current: safeCurrent,
          total: safeTotal,
        })}
      </span>
    </div>
  );
}
